/** What can be pinned to a person's page from their Direct thread, and who the page is. */
import type { DirectItem } from "../../shared/talk";
import type { Attachment, HostPinView } from "../../shared/types";
import type { PinItem } from "./PinSheet";
import { firstName, lineWhen } from "./when";

type Chat = Extract<DirectItem, { kind: "chat" }>;

const ATT_NAME: Record<Attachment["kind"], string> = { image: "A picture", video: "A video", file: "A file", voice: "A voice note" };

/** What a line is called in a list: its words, else its file. */
function labelOf(c: Pick<Chat, "body" | "attachments">): string {
  const words = c.body.replace(/\s+/g, " ").trim();
  if (words) return words;
  const a = c.attachments[0];
  return a ? (a.kind === "file" || a.kind === "image" ? a.name : ATT_NAME[a.kind]) : "";
}

const ATT_WORD: Record<Attachment["kind"], string> = { image: "picture", video: "video", file: "file", voice: "voice note" };

/** What a line is, whose it is and when: "Chat · Your message · Today 1:46 PM", "File · Priya's file · Tue 9:10 AM". */
function subOf(c: Chat, now: number): string {
  const what = c.body.trim() ? "message" : ATT_WORD[c.attachments[0].kind];
  const whose = c.who.kind === "host" ? "Your" : `${firstName(c.who.name)}'s`;
  return `${c.body.trim() ? "Chat" : "File"} · ${whose} ${what} · ${lineWhen(c.createdAt, now)}`;
}

/** A line that can be pinned: a chat line with words or a file, never a question, which the page shows by itself. */
export const pinnable = (i: DirectItem): i is Chat => i.kind === "chat" && !i.question && (!!i.body.trim() || i.attachments.length > 0);

/** Every line that can be pinned, newest first; a file is pinned through the line that carries it. */
export function pinItemsOf(items: DirectItem[]): PinItem[] {
  return items
    .filter(pinnable)
    .toSorted((a, b) => b.createdAt - a.createdAt)
    .map((c) => ({
      key: `message:${c.id}`,
      kind: "message",
      id: c.id,
      registrantId: c.registrantId,
      webinarId: c.webinarId,
      label: labelOf(c),
      sub: subOf(c, Date.now()),
      icon: c.body.trim() ? "chat" : "file",
    }));
}

/** Every registration the thread's lines belong to: the person's page in each room they hold one. */
export function registrantsOf(items: DirectItem[]): string[] {
  return [...new Set(items.flatMap((i) => (i.kind === "chat" ? [i.registrantId] : [])))];
}

export const pinKey = (p: Pick<HostPinView, "target_kind" | "target_id">) => `${p.target_kind}:${p.target_id}`;

/** The pin shown for this person: the newest made for them alone, on any of their registrations. */
export function personPin(pins: HostPinView[], registrantIds: readonly string[]): HostPinView | null {
  return pins.filter((p) => p.registrant_id !== null && registrantIds.includes(p.registrant_id)).toSorted((x, y) => y.created_at - x.created_at)[0] ?? null;
}
